import { Suspense, useMemo, useState } from 'react';
import { FormFIltersInformeDicom, GenerarInformeDicomDto, FileResponseInterface, MorososDicomKeyDto } from '../interfaces/index';
import { useColumnsInformeDicom } from '../utils';
import { useDataFetch } from '../../../../hooks/useDataFetch2';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import Tabla from "../../../../components/ui/materialui/serverSideTable";
import TableHeader from "../../../../components/ui/materialui/tableHeader";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import { exportExcel } from '../../../../utils/exportExcel';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { Alert, Grid2, Link } from '@mui/material';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { ResponseInformeDicomDto } from '../models';
import { generarCertificadoDicom } from '../services/informeDicomService';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';
import { TipoFuncionalidad } from '../../../../utils/enums';

const urlTabla = `siscred-api-reportes/v1/informes/get-mutuos-dicom`;

interface TableComponentInterface {
    formFilters: FormFIltersInformeDicom,
}

export const TablaComponent = ({ formFilters }: TableComponentInterface) => {

    const params = useMemo(() => {
        const p = new URLSearchParams();
        p.set("fechaEmision", formFilters.fecha!.toISOString().split("T")[0]);
        return p;
    }, [formFilters]);

    const dataTable = useDataFetch(
        urlTabla,
        params,
        true,
    );

    const [selectedKeys, setSelectedKeys] = useState<MorososDicomKeyDto[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const { COLUMNS } = useColumnsInformeDicom({
        controlTabla: dataTable,
        selectedKeys,
        setSelectedKeys,
        selectAll,
        setSelectAll,
    });
    const columns = useMemo(() => COLUMNS, [dataTable, selectedKeys, selectAll]);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => { },
    });

    const modalConfirmGeneracion = () => {
        setModalConfirmacion({
            show: true,
            title: 'Generar informe DICOM',
            message: '¿Está seguro de querer generar el informe?',
            metodo: generarCertificado,
        });
    };

    const generarCertificado = async () => {
        const body: GenerarInformeDicomDto = {
            fechaEmision: formFilters.fecha!.toISOString().split("T")[0],
            morosos: selectAll ? [] : selectedKeys,
            selectAll,
        };

        const response = await generarCertificadoDicom(body);

        if (response.status === 200) {
            handleDownload(response.data as FileResponseInterface);
            await dataTable.fetchData();
            setSelectedKeys([]);
            setSelectAll(false);
            setModalConfirmacion((prev) => ({ ...prev, show: false }));
        }

    };

    const handleDownload = (file: FileResponseInterface) => {
        // 1. Decodificar base64 → bytes
        const byteCharacters = atob(file.fileContent);
        const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        // 2. Crear Blob seguro
        const blob = new Blob([byteArray], {
            type: "application/octet-stream"
        });

        // 3. Crear URL temporal
        const url = URL.createObjectURL(blob);

        // 4. Crear link de descarga
        const link = document.createElement("a");
        link.href = url;
        link.download = file.fileName || "archivo.txt";
        document.body.appendChild(link);
        link.click();

        // 5. Limpieza
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const selectedCount = selectAll ? dataTable.totalRows : selectedKeys.length;
    const hasSelection = selectAll || selectedKeys.length > 0;

    const showSelectAllBanner = useMemo(() => {
        if (selectAll || dataTable.totalRows <= dataTable.perPage || dataTable.data.length === 0) return false;
        const pageRows = dataTable.data as ResponseInformeDicomDto[];
        return pageRows.every(row =>
            selectedKeys.some(k => k.rut === row.rut && k.nroOperacion === row.nroOperacion && k.nroDividendo === row.nroDividendo)
        );
    }, [selectAll, dataTable.totalRows, dataTable.perPage, dataTable.data, selectedKeys]);

    return (
        <CustomAccordion title={'Deudores y codeudores'} expandedDefault>
            <Suspense>
                <ConfirmationModal
                    onHide={() =>
                        setModalConfirmacion({
                            ...modalConfirmacion,
                            show: false,
                        })
                    }
                    onConfirm={modalConfirmacion.metodo}
                    show={modalConfirmacion.show}
                    title={modalConfirmacion.title}
                    message={modalConfirmacion.message}
                />

                {selectAll && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Seleccionados los {dataTable.totalRows} registros totales.{' '}
                        <Link
                            component="button"
                            onClick={() => { setSelectAll(false); setSelectedKeys([]); }}
                            sx={{ cursor: 'pointer' }}
                        >
                            Limpiar selección
                        </Link>
                    </Alert>
                )}

                {showSelectAllBanner && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Seleccionados {selectedKeys.length} registros de esta página.{' '}
                        <Link
                            component="button"
                            onClick={() => { setSelectAll(true); setSelectedKeys([]); }}
                            sx={{ cursor: 'pointer' }}
                        >
                            Seleccionar los {dataTable.totalRows} registros totales
                        </Link>
                    </Alert>
                )}

                <Tabla
                    columns={columns}
                    control={dataTable}
                    url={urlTabla}
                    rowsPerPageOptions={[5, 10, 25]}
                    titulo={
                        <>
                            <TableHeader
                                title=""
                                control={dataTable}
                                button={
                                    <Grid2 container spacing={1} sx={{ justifyContent: "flex-end", textAlign: "right" }}>
                                        <PermissionGuard permissions={[TipoFuncionalidad.Cobranza_adm]}>
                                            <MButton
                                                type="button"
                                                variant='contained'
                                                color='primary'
                                                disabled={!hasSelection}
                                                onClick={modalConfirmGeneracion}
                                            >
                                                Generar Certificado{selectedCount > 0 ? ` (${selectedCount})` : ''}
                                            </MButton>
                                        </PermissionGuard>
                                    </Grid2>
                                }
                                buttonExport={
                                    <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                        <MButton
                                            type="button"
                                            variant="outlined"
                                            onClick={() => exportExcel(dataTable.data, columns, "Productos")}
                                            startIcon={<SummarizeTwoToneIcon />}
                                        >
                                            Exportar
                                        </MButton>
                                    </PermissionGuard>
                                }
                            />
                        </>
                    }
                />
            </Suspense>
        </CustomAccordion>
    )
};